import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { concat, Observable, of, Subject } from 'rxjs';
import {
  startWith,
  catchError,
  distinctUntilChanged,
  switchMap,
  map,
  tap,
  takeUntil,
} from 'rxjs/operators';
import Swal from 'sweetalert2';

import { AuthService } from '@app/core/auth';
import { RfqService, RFQ } from '@app/core/quotes';
import { StaffService } from '@app/core/staff';
import { environment } from '@app/env-service';

import { CreateClientRfqComponent } from '../create-client-rfq/create-client-rfq.component';

@Component({
  selector: 'cnc-consolidate-master-sku',
  templateUrl: './consolidate-master-sku.component.html',
  styleUrls: ['./consolidate-master-sku.component.scss'],
})
export class ConsolidateMasterSkuComponent implements OnInit, OnDestroy {
  url :string = environment.url;
  loading :boolean = false;
  submitted :boolean = false;
  gettingRecords :boolean = false;
  formData :UntypedFormGroup;
  pageTitle :string = 'Move selected SKUS to new Master Sku';
  @Input() rfq_ids :any;
  @Input() firstSelectedRfqId :number = 0;

  rfq$ :Observable<any[]>;
  rfqLoading = false;
  rfqInput$ = new Subject<string>();
  retainSku :boolean = false;
  consolidatedSku$ :Observable<RFQ>;
  gettingSkus$ :Observable<boolean>;
  consolidatedRfq :RFQ = null;
  private onDestroy$ = new Subject();
  rfqs :RFQ[] = [];

  constructor(
    public activeModal :NgbActiveModal,
    private fb :UntypedFormBuilder,
    public rfqSvc :RfqService,
    private toastr :ToastrService,
    public staffService :StaffService,
    public authService :AuthService,
    private modal :NgbModal,
    private cd :ChangeDetectorRef,
  ) {}

  ngOnInit() :void {
    this.loadRfqsLookup();
    this.initActions();
    this.getDataFromService();
    this.getFirstConsolidatedSku().subscribe();
    this.getGettingSkusState().subscribe();
    this.getRfqs().subscribe();
    this.formData = this.fb.group({
      sku: [null, Validators.required],
    });
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSubmit() :void {
    this.loading = true;
    this.submitted = true;

    if (this.formData.invalid) {
      Swal.fire({
        title: 'Form Error!',
        text: 'Please fill all the required fields.',
        type: 'error',
        confirmButtonText: 'Back to Form',
      });
      this.loading = false;

      return;
    }

    const params = {
      selected_skus: this.rfq_ids,
      retainSku: this.retainSku,
    };

    this.rfqSvc
      .moveSkuToMasterSku(params, this.formData.controls.sku.value.id)
      .subscribe(
        (resp) => {
          this.toastr.success('Successfully moved skus to new master sku');
          this.loading = false;
          this.activeModal.close(resp);
        },
        (resp) => {
          const error = resp.error.error;
          this.loading = false;
          this.toastr.error(error);
        },
      );
  }

  private loadRfqsLookup() :void {
    this.rfq$ = concat(
      of([]),
      this.rfqInput$.pipe(
        startWith(''),
        distinctUntilChanged(),
        tap(() => (this.rfqLoading = true)),
        switchMap((term) =>
          this.rfqSvc.searchParentSKUDropdown({ sku: term }).pipe(
            catchError(() => of([])),
            tap(() => (this.rfqLoading = false)),
            map((resp) => resp),
          ),
        ),
      ),
    );
  }

  trackByFn(item :any) {
    return item.id;
  }

  initActions() :void {
    this.rfqSvc.getConsolidatedSku(this.firstSelectedRfqId);
  }

  getDataFromService() :void {
    this.consolidatedSku$ = this.rfqSvc.consolidatedSku$;
    this.gettingSkus$ = this.rfqSvc.gettingSkus$;
  }

  private getFirstConsolidatedSku() :Observable<RFQ> {
    return this.consolidatedSku$.pipe(
      tap((rfq :RFQ) => {
        this.consolidatedRfq = rfq;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getGettingSkusState() :Observable<boolean> {
    return this.gettingSkus$.pipe(
      tap((gettingSkus :boolean) => {
        this.loading = gettingSkus;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getRfqs() :Observable<RFQ[]> {
    return this.rfq$.pipe(
      tap((rfqs :RFQ[]) => {
        this.rfqs = rfqs;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  createMasterRfq() :void {
    const modalRef = this.modal.open(CreateClientRfqComponent, {
      size: 'lg',
      windowClass: 'xl1024',
    });
    modalRef.componentInstance.consolidatedRfq = this.consolidatedRfq;
    modalRef.result.then(
      (rfq :RFQ) => {
        this.formData.patchValue({
          sku: rfq,
        });
        this.rfqs.push(rfq);
        this.cd.detectChanges();
      },
      (error) => {},
    );
  }
}
